/**
 * Host (device) notification facade — the ONLY Vue-to-platform boundary for
 * realtime alerts and host push (phase6-contract.md §3.5). Components and
 * stores never touch `Notification`, the service worker or a Capacitor plugin
 * directly; they call this module (via `composables/usePermissions.ts` for
 * reactive state), and the realtime service calls it from its lifecycle.
 *
 * Two platforms, one five-way model (§3.5.2):
 *
 *   permission  — the OS display permission. Web: `Notification.permission`.
 *                 Native: `local-notifications.service` (`checkPermission()` /
 *                 `requestPermission()`, which keeps the Android exact-alarm
 *                 offer and the post-grant reminder reschedule) —
 *                 `PushNotifications.checkPermissions()` is NOT used for it
 *                 (always `granted` on Android ≤ 12). `unsupported` when the
 *                 build flag is off, on an insecure context, or with no API.
 *   preference  — the user's explicit Enable/Disable for THIS device
 *                 (`push.service.ts`, `syntry.device_notifications.v1`).
 *   push        — the endpoint axis (`push.service.ts`): unavailable / off /
 *                 active / invalid.
 *   policy      — the tenant policy (`push.service.ts`, DEC-097).
 *   state       — ERPat's six: unsupported → denied → default → invalid →
 *                 granted → disabled. `granted` + `unset` preference renders
 *                 `disabled` (DEC-059 parity: a remembered permission is not
 *                 consent for background delivery).
 *
 * The server switch (`preferences.notifications`, the page's General toggle)
 * is the composable's axis: it is read on entering the page and gates
 * `enable()` there, never here.
 *
 * Alerts (Phase 5 rule, unchanged): a device alert is shown ONLY while the
 * app is hidden — in the foreground the in-app toast is the surface — and,
 * once this installation holds a push registration, the OS/worker owns
 * presentation (ERPat DEC-062 parity), so the page-context alert steps aside.
 *
 * Lifecycle (§3.7): `prepare()` runs at BOOT (the native tap listener, the
 * push listeners, the foreground triggers) so a cold-start tap is parked
 * instead of lost; `release()` is app-teardown only.
 */
import { readonly, shallowRef, type Ref } from 'vue';
import { Capacitor } from '@capacitor/core';
import type { RouteLocationRaw } from 'vue-router';
import type { RealtimeEnvelope } from '@/contracts/realtime-envelope-v1';
import { resolveRealtimeTarget } from '@/contracts/realtime-routes';
import { HOST_NOTIFICATION_ICON } from '@/config/notifications';
import { messageAlertCopy, privateFallbackCopy } from '@/i18n/notification-copy';
import * as localNotifications from '@/services/device/local-notifications.service';
import * as push from '@/services/device/push.service';
import * as appState from '@/services/device/app-state.service';
import { rescheduleEventReminders } from '@/services/event-reminders';
import { primeNotificationSound } from '@/services/notification-sound';
import { navigateToRealtimeTarget } from '@/services/realtime-navigation';
import { tenantStamp } from '@/services/session-scope';
import { HOST_NOTIFICATIONS_ENABLED, IS_DEV } from '@/utils/env';

export type { DevicePreference, HostNotificationsInfo, HostPolicy, PushState, PushTap } from '@/services/device/push.service';

/**
 * `unsupported` — no usable API here (the build flag is off, an insecure
 * context, a browser without `Notification`, or a native shell without the
 * plugin). The other three mirror the OS permission.
 */
export type DevicePermissionState = 'unsupported' | 'default' | 'granted' | 'denied';

/** The rendered state — ERPat's six (§3.5.2). */
export type DeviceNotificationState = 'unsupported' | 'denied' | 'default' | 'invalid' | 'granted' | 'disabled';

/** The five axes plus the rendered state, as one snapshot. */
export interface DeviceNotificationStatus {
  permission: DevicePermissionState;
  preference: push.DevicePreference;
  push: push.PushState;
  policy: push.HostPolicy;
  state: DeviceNotificationState;
}

/** Android channel for live alerts — distinct from the reminder engine's. */
const REALTIME_CHANNEL: localNotifications.LocalNotificationChannel = {
  id: 'syntry-realtime',
  name: 'Live updates',
  description: 'New notifications and messages from ERPat.',
};

/** `extra.source` stamped on every realtime alert (see local-notifications.service.ts). */
const REALTIME_SOURCE = 'syntry-realtime';

/** Called with the `event_id` when the user opens an alert (web click or native tap). */
let openedHook: ((eventId: string) => void) | null = null;
let prepared = false;
let detachNativeTap: (() => void) | null = null;
let detachAppState: (() => void) | null = null;
let triggersAttached = false;
/**
 * The last OS permission read. The web value is live (synchronous); the
 * native one is what the last `checkPermission()` answered — `default` until
 * the first read, which `prepare()` / `recheck()` perform.
 */
let permissionCache: DevicePermissionState = 'default';

/* ── Permission ────────────────────────────────────────────────────────────── */

/** True when the Web Notification API can be used from this page. */
function webNotificationsAvailable(): boolean {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') return false;
  // Browsers only grant notification permission to secure contexts; asking
  // from http://<lan-ip> would silently fail, so report it honestly instead.
  if (window.isSecureContext === false) return false;
  return typeof Notification.requestPermission === 'function';
}

function webPermission(): DevicePermissionState {
  switch (Notification.permission) {
    case 'granted':
      return 'granted';
    case 'denied':
      return 'denied';
    default:
      return 'default';
  }
}

function fromLocal(value: localNotifications.LocalNotificationPermission): DevicePermissionState {
  switch (value) {
    case 'granted':
      return 'granted';
    case 'denied':
      return 'denied';
    case 'prompt':
      return 'default';
    default:
      return 'unsupported';
  }
}

/**
 * Current permission state, synchronously (cheap — safe to call on every
 * recheck). Native answers from the cache filled by {@link readPermission}.
 */
export function getPermissionState(): DevicePermissionState {
  if (!HOST_NOTIFICATIONS_ENABLED) return 'unsupported';
  if (Capacitor.isNativePlatform()) {
    return localNotifications.isSupported() ? permissionCache : 'unsupported';
  }
  if (!webNotificationsAvailable()) return 'unsupported';
  return webPermission();
}

/** Re-read the OS permission (the native plugin call is async) and cache it. */
export async function readPermission(): Promise<DevicePermissionState> {
  if (!HOST_NOTIFICATIONS_ENABLED) return 'unsupported';
  if (Capacitor.isNativePlatform()) {
    permissionCache = fromLocal(await localNotifications.checkPermission());
    return getPermissionState();
  }
  return getPermissionState();
}

/**
 * Ask for permission. MUST be called from a user gesture (a tap on the
 * settings-page button): browsers ignore or auto-deny prompts fired from a
 * timer, a promise continuation or page load, and Chrome quietly downgrades
 * repeated non-gesture asks into a "blocked by default" state. On native the
 * request goes through the local-notifications service (display permission +
 * the Android exact-alarm offer) and a grant re-arms the event reminders that
 * were skipped while permission was missing.
 */
export async function requestPermission(): Promise<DevicePermissionState> {
  const current = await readPermission();
  if (current !== 'default') return current;

  if (Capacitor.isNativePlatform()) {
    permissionCache = fromLocal(await localNotifications.requestPermission());
    if (permissionCache === 'granted') rescheduleEventReminders();
    settle();
    return getPermissionState();
  }

  const result = await new Promise<NotificationPermission>((resolve) => {
    let done = false;
    const finish = (value: NotificationPermission): void => {
      if (done) return;
      done = true;
      resolve(value);
    };
    try {
      // Legacy Safari (< 16) still uses the callback form and returns
      // `undefined` rather than a promise; support both so no runtime hangs.
      const maybePromise = Notification.requestPermission((permission) => finish(permission));
      if (maybePromise && typeof maybePromise.then === 'function') {
        maybePromise.then(finish, () => finish(Notification.permission));
      }
    } catch {
      finish(Notification.permission);
    }
  });
  const next = result === 'granted' ? 'granted' : result === 'denied' ? 'denied' : 'default';
  settle();
  return next;
}

/* ── The five-way model ────────────────────────────────────────────────────── */

/**
 * The rendered state: unsupported → denied → default → invalid → granted →
 * disabled. A granted permission with an `unset` preference renders `disabled`.
 */
export function state(): DeviceNotificationState {
  const permission = getPermissionState();
  if (permission === 'unsupported' || permission === 'denied' || permission === 'default') return permission;
  if (push.readPreference() === 'enabled') return push.isInvalid() ? 'invalid' : 'granted';
  return 'disabled';
}

/** One snapshot of every axis. */
export function status(): DeviceNotificationStatus {
  return {
    permission: getPermissionState(),
    preference: push.readPreference(),
    push: push.pushState(),
    policy: push.policy(),
    state: state(),
  };
}

const statusRef = shallowRef<DeviceNotificationStatus>(safeStatus());

function safeStatus(): DeviceNotificationStatus {
  try {
    return status();
  } catch {
    return { permission: 'unsupported', preference: 'unset', push: 'unavailable', policy: 'off', state: 'unsupported' };
  }
}

/**
 * The reactive snapshot the composable projects. Updated by {@link settle}
 * after every action, recheck and bootstrap — so a page stays current when
 * the realtime service re-checks from its own lifecycle.
 */
export const deviceStatus: Readonly<Ref<DeviceNotificationStatus>> = readonly(statusRef);

/** Recompute the snapshot (cheap: storage reads + the cached permission). */
export function settle(): DeviceNotificationStatus {
  const next = safeStatus();
  const prev = statusRef.value;
  if (
    prev.permission !== next.permission ||
    prev.preference !== next.preference ||
    prev.push !== next.push ||
    prev.policy !== next.policy ||
    prev.state !== next.state
  ) {
    statusRef.value = next;
  }
  return next;
}

/* ── The two user actions + the rechecks ───────────────────────────────────── */

/**
 * Enable (gesture only — the ONLY caller of {@link requestPermission}):
 * request → preference `enabled` → registration when the policy allows.
 * The composable refuses before calling this while the server switch is off.
 */
export async function enable(): Promise<DeviceNotificationState> {
  const permission = await requestPermission();
  if (permission !== 'granted') {
    if (permission === 'denied') push.clearInvalid();
    settle();
    return state();
  }
  push.writePreference('enabled');
  push.clearInvalid();
  settle();
  if (push.policy() === 'available') {
    await push.ensureRegistration();
  }
  return settle().state;
}

/**
 * Disable: preference `disabled`, then ALWAYS revoke — a record cleared by a
 * 404 does not mean the server row and the provider token are gone, and both
 * calls are idempotent. Never touches the server switch.
 */
export async function disable(): Promise<DeviceNotificationState> {
  push.writePreference('disabled');
  push.clearInvalid();
  settle();
  await push.revoke();
  return settle().state;
}

/**
 * Re-read the facts — never prompts. The permission read is un-throttled; the
 * registration re-check runs (throttled 60 s, un-throttled with `force`) only
 * while `granted` + `enabled` + policy available. A `denied` permission drops
 * the records and the preference (ERPat parity). Runs on focus /
 * visibilitychange / native resume (attached in {@link prepare}), at realtime
 * start (W1) and after a bootstrap ({@link applyBootstrap}).
 */
export function recheck(force = false): void {
  void (async () => {
    const permission = await readPermission();
    if (permission === 'denied') {
      push.clearLocal();
      push.clearPreference();
    } else if (state() === 'granted' && push.policy() === 'available') {
      await (force ? push.refresh() : push.ensureRegistration({ throttled: true }));
    }
    settle();
  })().catch((err: unknown) => {
    if (IS_DEV) console.warn('[notifications] recheck', err);
  });
}

/**
 * Make sure this installation holds a registration the server accepted
 * (un-throttled; single-flight in `push.service.ts`). Resolves to the state.
 */
export async function ensureRegistration(): Promise<DeviceNotificationState> {
  await readPermission();
  if (state() === 'granted' && push.policy() === 'available') {
    await push.ensureRegistration();
  }
  return settle().state;
}

/** {@link ensureRegistration} un-throttled — after the worker re-subscribed. */
export async function refresh(): Promise<DeviceNotificationState> {
  await readPermission();
  if (state() === 'granted' && push.policy() === 'available') {
    await push.refresh();
  }
  return settle().state;
}

/** Revoke this installation's endpoint (bounded 3 s) — BEFORE sign-out (DEC-090). */
export async function revoke(): Promise<void> {
  await push.revoke();
  settle();
}

/** Drop the two local push records — in `signOutLocal()` (§3.1). */
export function clearLocal(): void {
  push.clearLocal();
  settle();
}

/**
 * Adopt a bootstrap's `host_notifications` block (the realtime service calls
 * this after every bootstrap; `null` on stop) and re-check un-throttled — the
 * policy and the VAPID key only exist after the bootstrap answered.
 */
export function applyBootstrap(host: push.HostNotificationsInfo | null): void {
  push.setHostNotifications(host);
  if (host) recheck(true);
  else settle();
}

/* ── Lifecycle hooks ───────────────────────────────────────────────────────── */

/** Register (or clear) the callback run when the user opens an alert. */
export function setOpenedHook(fn: ((eventId: string) => void) | null): void {
  openedHook = fn;
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'visible') recheck();
}

function onFocus(): void {
  recheck();
}

/**
 * Boot-time preparation, idempotent (§3.7): the realtime alert channel and its
 * tap listener, the push plugin listeners + tap routing, the foreground
 * triggers (web `visibilitychange` / `focus`, native `appStateChange`), and a
 * first permission read. Cheap and safe to call more than once.
 */
export function prepare(): void {
  if (prepared) return;
  prepared = true;

  // Arm the in-app chime's autoplay unlock. It has to ride a real user gesture,
  // and this is the boot-level "arm the alert surfaces" call — the same place
  // the tap listeners and the foreground triggers are attached.
  primeNotificationSound();

  push.prepare((tap) => openTarget(tap.target, tap.eventId, tap.tenant));

  if (localNotifications.isSupported()) {
    void localNotifications.ensureChannelFor(REALTIME_CHANNEL);
    void localNotifications
      .onTapFor(REALTIME_SOURCE, (extra) => {
        const eventId = typeof extra.eventId === 'string' ? extra.eventId : '';
        const tenant = typeof extra.tenant === 'string' ? extra.tenant.trim().toLowerCase() : '';
        openTarget(parseTarget(extra.route), eventId, tenant);
      })
      .then((detach) => {
        // A release() that raced the attach must still detach it.
        if (prepared) detachNativeTap = detach;
        else detach();
      });
    void appState
      .onAppStateChange((active) => {
        if (active) recheck();
      })
      .then((detach) => {
        if (prepared) detachAppState = detach;
        else detach();
      });
  }

  if (!triggersAttached && typeof document !== 'undefined') {
    triggersAttached = true;
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
  }

  void readPermission().then(() => settle());
}

/** Undo {@link prepare} — app teardown only (never at realtime stop). */
export function release(): void {
  prepared = false;
  openedHook = null;
  push.release();
  if (detachNativeTap) {
    detachNativeTap();
    detachNativeTap = null;
  }
  if (detachAppState) {
    detachAppState();
    detachAppState = null;
  }
  if (triggersAttached && typeof document !== 'undefined') {
    triggersAttached = false;
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('focus', onFocus);
  }
}

/* ── Rendering ─────────────────────────────────────────────────────────────── */

/**
 * Title/body for an alert. Message events are content-free on the wire by
 * policy (the envelope carries no text), so they get the fixed message copy;
 * a notification without renderable text gets the private fallback.
 */
function alertCopy(envelope: RealtimeEnvelope): { title: string; body: string } {
  if (envelope.type === 'message.created' || envelope.type === 'message.read') {
    return messageAlertCopy();
  }
  const fallback = privateFallbackCopy();
  const notification = envelope.notification;
  const title = notification?.title.trim() || fallback.title;
  const body = notification?.body.trim() || (notification?.title.trim() ? '' : fallback.body);
  return { title, body };
}

/**
 * Focus this window (web) and route to the alert's target through the ONE
 * router entry for realtime targets (§3.7), then report the open. `tenant` is
 * the stamp the alert was created under (`extra.tenant` / `data.tenant`);
 * `navigateToRealtimeTarget()` compares it with the current session and parks
 * the target (not signed in) or refuses it (another company) instead of
 * landing on a foreign tenant's page. An EMPTY stamp means the source could
 * not carry one (a native push payload before Phase 7 adds `tenant_scope`,
 * DEC-094; a worker click before the page posted `syntry:sw-config`): the
 * alert was delivered to THIS installation's registration, so it is
 * attributed to the current session — the same fallback the parameter
 * default expresses and the worker itself applies. The user id is never
 * known here (`''`). The opened hook fires only on a real navigation; a
 * parked target records its receipt when the consumer replays it.
 */
function openTarget(target: RouteLocationRaw, eventId: string, tenant: string = tenantStamp()): void {
  try {
    window.focus();
  } catch {
    /* some embedders refuse focus() — navigation below still applies */
  }
  const result = navigateToRealtimeTarget(target, { tenant: tenant || tenantStamp(), userId: '', eventId: eventId || null });
  if (result === 'navigated' && eventId && openedHook) {
    try {
      openedHook(eventId);
    } catch {
      /* a receipt must never break navigation */
    }
  }
}

/** The route stored in a native notification's `extra`, or the inbox when unreadable. */
function parseTarget(raw: unknown): RouteLocationRaw {
  if (typeof raw !== 'string' || raw === '') return { name: 'inbox' };
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed as RouteLocationRaw;
  } catch {
    /* fall through */
  }
  return { name: 'inbox' };
}

/** FNV-1a over the event id → a stable positive 32-bit notification id. */
function nativeIdOf(eventId: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < eventId.length; i += 1) {
    hash ^= eventId.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash & 0x7fffffff;
}

/**
 * Some browsers (Chrome for Android among them) throw "Illegal constructor"
 * for `new Notification()` in page context and require the service-worker
 * form. In a production PWA a registration exists, so fall back to it (the
 * worker's `notificationclick` routes it — §3.8). In dev (no worker) the
 * in-app toast is the only surface on those browsers.
 */
async function showViaServiceWorker(title: string, options: NotificationOptions): Promise<void> {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    await registration?.showNotification(title, options);
  } catch {
    /* no registration / not allowed — nothing more to do */
  }
}

function showWeb(envelope: RealtimeEnvelope, title: string, body: string, target: RouteLocationRaw): void {
  if (getPermissionState() !== 'granted') return;
  const tenant = tenantStamp();
  const options: NotificationOptions = {
    body,
    // Collapses a redelivered event into one OS notification.
    tag: envelope.event_id,
    icon: HOST_NOTIFICATION_ICON,
    data: { eventId: envelope.event_id, tenant },
  };
  try {
    const handle = new Notification(title, options);
    handle.onclick = () => {
      openTarget(target, envelope.event_id, tenant);
      handle.close();
    };
  } catch {
    void showViaServiceWorker(title, options);
  }
}

async function showNative(envelope: RealtimeEnvelope, title: string, body: string, target: RouteLocationRaw): Promise<void> {
  prepare();
  if ((await localNotifications.checkPermission()) !== 'granted') return;
  await localNotifications.showNow({
    id: nativeIdOf(envelope.event_id),
    title,
    body,
    channelId: REALTIME_CHANNEL.id,
    extra: {
      source: REALTIME_SOURCE,
      eventId: envelope.event_id,
      route: JSON.stringify(target),
      tenant: tenantStamp(),
    },
  });
}

/**
 * Show a host notification for an envelope — ONLY while the app is hidden
 * (the in-app toast covers the foreground), and only while this installation
 * holds NO push registration (with one, the OS / worker presents the push and
 * a page-context alert would double it — DEC-062 parity). Title and body are
 * handed to the OS as plain strings; they are never interpreted as HTML.
 */
export function show(envelope: RealtimeEnvelope): void {
  if (!HOST_NOTIFICATIONS_ENABLED) return;
  if (typeof document !== 'undefined' && document.visibilityState !== 'hidden') return;
  if (push.pushActive()) return;
  const { title, body } = alertCopy(envelope);
  const target = resolveRealtimeTarget(envelope);
  if (Capacitor.isNativePlatform()) {
    showNative(envelope, title, body, target).catch((err: unknown) => {
      if (IS_DEV) console.warn('[notifications] native alert failed', err);
    });
    return;
  }
  showWeb(envelope, title, body, target);
}

/** W5: worker messages — re-exported so the subscription can target the facade. */
export const handleWorkerMessage = push.handleWorkerMessage;

/** Grouped export for callers that prefer one object (mirrors the service style). */
export const notificationsDevice = {
  getPermissionState,
  readPermission,
  requestPermission,
  state,
  status,
  deviceStatus,
  settle,
  enable,
  disable,
  recheck,
  ensureRegistration,
  refresh,
  revoke,
  clearLocal,
  applyBootstrap,
  show,
  prepare,
  release,
  setOpenedHook,
  handleWorkerMessage,
};
